import { Box } from "@mui/material"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useMemo, useState } from "react"
import { playerStore, usePlayList } from "src/store/PlayerStore"

import useTranslate from "../../../../hooks/use-translate"
import { mapAudioTrackName, s } from "../../../../utilities/common"
import { fontSizeFactor } from "../../../../utilities/constants"
import { AudioStreamQualities } from "../../../../utilities/streaming-helpers"
import { theme, TI } from "../../../../utilities/theme"
import { DGText } from "../../../common/DGText"
import Atmos from "../../../images/atmos.png"
import { PlayerUIFocusKeys } from "../../player-ui-focus-keys"
import { StreamingQualitySelect } from "../SelectBoxes/StreamingQualitySelect"
import { SubSelect } from "../SelectBoxes/SubSelect"
import { FavoriteButton } from "../UIButtons/FavoriteButton"
import { PlayButton } from "../UIButtons/PlayButton"
import { ProgressLeftButton } from "../UIButtons/ProgressLeftButton"
import { ProgressRightButton } from "../UIButtons/ProgressRightButton"
import { RepeatButton } from "../UIButtons/RepeatButton"
import { ShuffleButton } from "../UIButtons/ShuffleButton"
import { StreamQualityButton } from "../UIButtons/StreamQualityButton"
import { SubButton } from "../UIButtons/SubButton"
import { TracksButton } from "../UIButtons/TracksButton"

const ButtonGroupMiddle = () => {
  const content = playerStore((state) => state.content)
  const playList = usePlayList(content)
  const isPreview = playerStore((state) => state.isPreview)
  const { focusKey, ref } = useFocusable({
    focusKey: PlayerUIFocusKeys.ButtonGroupMiddle,
    saveLastFocusedChild: true,
  })
  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          "& > :not(:last-child)": {
            mr: s("32px"),
          },
          alignItems: "center",
        }}
      >
        {content?.__typename !== "LiveConcert" && !isPreview ? (
          <ProgressLeftButton
            type={
              content?.__typename === "Album" ||
              content?.__typename === "EditorialPlaylist"
                ? "title"
                : playList
                  ? "chapter"
                  : "progress"
            }
          />
        ) : null}
        <PlayButton focusKey={PlayerUIFocusKeys.PlayButton} />
        {content?.__typename !== "LiveConcert" && !isPreview ? (
          <ProgressRightButton
            type={
              content?.__typename === "Album" ||
              content?.__typename === "EditorialPlaylist"
                ? "title"
                : playList
                  ? "chapter"
                  : "progress"
            }
          />
        ) : null}
      </Box>
    </FocusContext.Provider>
  )
}

const ButtonGroupLeft = () => {
  const isPreview = playerStore((state) => state.isPreview)

  const subtitles = playerStore((state) => state.subtitles)
  const hasSubtitles = useMemo(
    () => subtitles && !!subtitles.length,
    [subtitles],
  )
  const hls = playerStore((state) => state.hls)

  const audioTrack = playerStore((state) => state.audioTrack)
  const t = useTranslate()

  const { focusKey, ref } = useFocusable({
    focusable:
      !isPreview &&
      (hasSubtitles || (hls?.allAudioTracks && hls.allAudioTracks.length > 0)),
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: PlayerUIFocusKeys.ButtonGroupLeft,
    saveLastFocusedChild: false,
    preferredChildFocusKey: PlayerUIFocusKeys.StreamQuality,
  })

  const mappedAudioTrackName = useMemo(
    () => mapAudioTrackName(audioTrack),
    [audioTrack],
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          width: s("500px"),
        }}
      >
        <Box
          sx={{
            borderRadius: "9999px",
            backgroundColor: theme.palette.new.playerButton,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            mr: s("50px"),
          }}
        >
          {!isPreview && (
            <>
              {hasSubtitles && (
                <SubButton focusKey={PlayerUIFocusKeys.SubButton} />
              )}
              {hls?.allAudioTracks && hls.allAudioTracks.length > 0 && (
                <StreamQualityButton
                  focusKey={PlayerUIFocusKeys.StreamQuality}
                />
              )}
            </>
          )}
        </Box>
        {mappedAudioTrackName === AudioStreamQualities.lossless && (
          <Box
            sx={{
              border: s("2px solid white"),
              paddingTop: s("3px"),
              paddingBottom: s("3px"),
              paddingLeft: s("6px"),
              paddingRight: s("6px"),
              borderRadius: s("6px"),
              opacity: 0.5,
            }}
          >
            <DGText
              tx={TI.Caption01tv}
              sx={{
                color: theme.palette.white.main,
                textTransform: "uppercase",
              }}
            >
              {t("settings__streaming_audio_lossless")}
            </DGText>
          </Box>
        )}
        {mappedAudioTrackName === AudioStreamQualities.atmos && (
          <Box component={"img"} sx={{ width: s("97px") }} src={Atmos} />
        )}
      </Box>
    </FocusContext.Provider>
  )
}

const ButtonGroupRight = () => {
  const content = playerStore((state) => state.content)
  const isPreview = playerStore((state) => state.isPreview)

  const hasPlaylist = useMemo(
    () =>
      content &&
      (content.__typename === "VodConcert" ||
        content.__typename === "Album" ||
        content.__typename === "EditorialPlaylist"),
    [content],
  )
  const { focusKey, ref } = useFocusable({
    focusable: !isPreview && hasPlaylist,
    isFocusBoundary: true,
    focusBoundaryDirections: ["right"],
    focusKey: PlayerUIFocusKeys.ButtonGroupRight,
    // preferredChildFocusKey: PlayerUIFocusKeys.Shuffle,
    saveLastFocusedChild: false,
  })

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          width: s("500px"),
        }}
      >
        <Box
          sx={{
            borderRadius: "9999px",
            backgroundColor: theme.palette.new.playerButton,
            display: "flex",
            flexDirection: "row",
          }}
        >
          {!isPreview && hasPlaylist && (
            <>
              {(content?.__typename === "Album" ||
                content?.__typename === "EditorialPlaylist") && (
                <>
                  <ShuffleButton focusKey={PlayerUIFocusKeys.Shuffle} />
                  <RepeatButton focusKey={PlayerUIFocusKeys.Repeat} />
                </>
              )}
              <TracksButton focusKey={PlayerUIFocusKeys.TrackButton} />
              <FavoriteButton focusKey={PlayerUIFocusKeys.Favorite} />
            </>
          )}
        </Box>
      </Box>
    </FocusContext.Provider>
  )
}

export const UISection = () => {
  const subtitleSelectOpen = playerStore((state) => state.subtitleSelectOpen)

  const streamingQualityOpenStore = playerStore(
    (state) => state.streamingQualityOpen,
  )
  const setStreamingQualityOpenStore = playerStore(
    (state) => state.setStreamingQualityOpen,
  )
  const [streamingQualityOpen, setStreamingQualityOpen] = useState(false)

  useEffect(() => {
    setStreamingQualityOpen(streamingQualityOpenStore)
  }, [streamingQualityOpenStore])
  useEffect(() => {
    setStreamingQualityOpenStore(streamingQualityOpen)
  }, [streamingQualityOpen])

  const { focusKey, ref } = useFocusable({
    focusKey: PlayerUIFocusKeys.PlayerUI,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right", "down"],
  })

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          width: "100%",
          height: s("112px"),
          marginTop: s("2px"),
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <ButtonGroupLeft />
        <ButtonGroupMiddle />
        <ButtonGroupRight />
      </Box>
      {subtitleSelectOpen && (
        <Box
          sx={{
            position: "absolute",
            left: s("-50px"),
            bottom: s("150px"),
            fontSize: s("36px", false, fontSizeFactor),
          }}
        >
          <SubSelect />
        </Box>
      )}
      {streamingQualityOpen && (
        <Box
          sx={{
            position: "absolute",
            left: s("-50px"),
            bottom: s("150px"),
            fontSize: s("36px", false, fontSizeFactor),
          }}
        >
          <StreamingQualitySelect />
        </Box>
      )}
    </FocusContext.Provider>
  )
}
